import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { styles, colors, fmtRole, useIsMobile, mobile } from '../components/UI';
import ChangePasswordModal from '../components/ChangePasswordModal';
import { MyPhoneEditor } from '../components/MyPhone';
import ErrorBoundary from '../components/ErrorBoundary';
import GlobalSearch from '../components/GlobalSearch';
import ViewAsModal from '../components/ViewAsModal';
import WhatsNew from '../components/WhatsNew';
import { OTHER_SYSTEMS } from '../utils/otherSystems';

// Top-level nav restructured 2026-05-14 into 4 buckets:
//   Dashboard | Sales ▾ | Operations | Admin
// "Sales" is a click-to-open dropdown holding the 5 day-to-day rep pages
// (Activity Log, Accounts, Customers, Projects, Calendar). Operations
// stays a direct link until it grows beyond one page. Admin is a direct
// link to the Admin page (which has its own grouped sub-tabs).
//
// Each entry's `roles` controls visibility per role. The Sales bucket is
// hidden entirely from ops users; Operations is hidden from rep-only.
const NAV = [
  { kind: 'link',  to: '/dashboard',  label: 'Dashboard',  roles: ['admin','leadership','supervisor','rep'] },
  { kind: 'group', label: 'Sales', roles: ['admin','leadership','supervisor','rep'], children: [
    { to: '/call-log',  label: 'Activity Log' },
    { to: '/accounts',  label: 'Accounts' },
    { to: '/customers', label: 'Customers' },
    { to: '/projects',  label: 'Projects' },
    { to: '/calendar',  label: 'Calendar' }
  ]},
  // Ops-only direct link: ops can't see the Sales dropdown but needs the
  // Customers page (read-only) to download badging documents (2026-06-11).
  { kind: 'link',  to: '/customers',  label: 'Customers',  roles: ['ops'] },
  { kind: 'link',  to: '/requests',   label: 'Requests',   roles: ['admin','leadership','supervisor','rep'] },
  // RM sales quote PDF / Excel template -> SSS-branded quote PDF (2026-10-28).
  { kind: 'link',  to: '/rm-quotes',  label: 'RM Quotes',  roles: ['admin','leadership','supervisor','rep'] },
  { kind: 'link',  to: '/operations', label: 'Operations', roles: ['admin','leadership','supervisor','ops'] },
  // Sales Library is company-wide collateral — visible to every role incl. ops.
  { kind: 'link',  to: '/sales-library', label: 'Sales Library', roles: ['admin','leadership','supervisor','rep','ops'] },
  { kind: 'link',  to: '/manuals', label: '📖 Manuals', roles: ['admin','leadership','supervisor','rep','ops'] },
  { kind: 'link',  to: '/estimating', label: 'Estimating', roles: ['admin','leadership'] },
  { kind: 'link',  to: '/price-book', label: 'Price Book', roles: ['admin','leadership'] },
  { kind: 'link',  to: '/admin',      label: 'Admin',      roles: ['admin','leadership'] }
];

export default function Layout() {
  const { user, logout, exitViewAs } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [menuOpen, setMenuOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [viewAsOpen, setViewAsOpen] = useState(false);
  // Which nav group dropdown is currently open (label string), or null.
  const [openGroup, setOpenGroup] = useState(null);
  const groupRef = useRef(null);

  const onLogout = () => { logout(); nav('/login'); };
  const onExitPreview = async () => { await exitViewAs(); window.location.href = '/'; };

  // Admin / leadership can View As anyone. Supervisor can View As their
  // direct reports (server enforces the relationship). Reps + ops can't.
  const roleCanImpersonate = (r) => r === 'admin' || r === 'leadership' || r === 'supervisor';
  const canImpersonate = user.actual_user
    ? roleCanImpersonate(user.actual_user.role)
    : roleCanImpersonate(user.role);
  const visibleNav = NAV.filter(n => n.roles.includes(user.role));

  // Close any open dropdown when route changes (the navigation succeeded,
  // dismissing the menu is the natural next step).
  useEffect(() => { setOpenGroup(null); }, [location.pathname]);

  // Click-outside to close the group dropdown. Tracks the wrapping div so
  // clicks INSIDE the dropdown (on a NavLink) close via the location
  // effect above, but clicks elsewhere also dismiss.
  useEffect(() => {
    if (!openGroup) return;
    const onDocClick = (e) => {
      if (groupRef.current && !groupRef.current.contains(e.target)) {
        setOpenGroup(null);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [openGroup]);

  // A group is "active" when the current route matches one of its children.
  // Used to highlight 'Sales ▾' the same way an active link is highlighted
  // when you're sitting on /accounts, /projects, etc.
  const isGroupActive = (group) =>
    group.children?.some(c => location.pathname.startsWith(c.to));

  return (
    <>
      <div style={isMobile ? { ...styles.topbar, ...mobile.topbar } : styles.topbar}>
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 24 }}>
          {isMobile && (
            <button
              onClick={() => setDrawerOpen(o => !o)}
              aria-label="Toggle menu"
              style={mobile.hamburger}
            >
              {drawerOpen ? '✕' : '☰'}
            </button>
          )}
          {/* Mobile sizes are load-bearing: the full-size wordmark + CRM label
              + role chip put the topbar's minimum width near 500px, which
              shoved the role chip out of the dark bar (white text on the white
              page) and forced every page wider than the phone viewport. */}
          <NavLink to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
            <img src="/sss-logo.jpg" alt="Select Safety Services" style={{ height: isMobile ? 26 : 36, borderRadius: 4, display: 'block' }} />
            {!isMobile && <span style={{ color: '#fff', fontSize: 11, fontWeight: 700, letterSpacing: 1.5 }}>CRM</span>}
          </NavLink>
          {!isMobile && (
            <nav ref={groupRef} style={{ display: 'flex', gap: 4, position: 'relative' }}>
              {visibleNav.map(n => {
                if (n.kind === 'link') {
                  return (
                    <NavLink
                      key={n.to} to={n.to}
                      style={({ isActive }) => isActive
                        ? { ...styles.navLink, ...styles.navLinkActive }
                        : styles.navLink}
                    >
                      {n.label}
                    </NavLink>
                  );
                }
                // Group: button toggles a dropdown of children.
                const active = isGroupActive(n);
                const open = openGroup === n.label;
                return (
                  <div key={n.label} style={{ position: 'relative' }}>
                    <button
                      type="button"
                      onClick={() => setOpenGroup(o => o === n.label ? null : n.label)}
                      style={{
                        ...styles.navLink,
                        ...(active ? styles.navLinkActive : {}),
                        background: open ? 'rgba(227,111,44,0.15)' : (active ? styles.navLinkActive.background : 'transparent'),
                        border: 'none', cursor: 'pointer', font: 'inherit'
                      }}
                    >
                      {n.label} <span style={{ fontSize: 10, marginLeft: 2 }}>▾</span>
                    </button>
                    {open && (
                      <div style={{
                        position: 'absolute', top: '100%', left: 0, marginTop: 4,
                        background: '#fff', border: `1px solid ${colors.border}`, borderRadius: 6,
                        minWidth: 180, boxShadow: '0 8px 20px rgba(0,0,0,0.15)', zIndex: 30,
                        overflow: 'hidden'
                      }}>
                        {n.children.map(c => (
                          <NavLink
                            key={c.to} to={c.to}
                            style={({ isActive }) => ({
                              display: 'block', padding: '10px 14px', fontSize: 13,
                              color: isActive ? colors.orange : '#111827',
                              background: isActive ? '#FFF7ED' : '#fff',
                              fontWeight: isActive ? 700 : 500,
                              textDecoration: 'none',
                              borderBottom: `1px solid ${colors.border}`
                            })}
                          >
                            {c.label}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              {/* Other Systems -- external links to the rest of the SSS fleet,
                  same dropdown idiom as Sales, closed by default. Plain anchors
                  in a new tab, never NavLink. Every CRM role is internal staff
                  (no customer login exists), and this Layout only renders
                  behind <Protected>. List + rules live in utils/otherSystems.js. */}
              <div style={{ position: 'relative' }}>
                <button
                  type="button"
                  onClick={() => setOpenGroup(o => o === 'Other Systems' ? null : 'Other Systems')}
                  style={{
                    ...styles.navLink,
                    background: openGroup === 'Other Systems' ? 'rgba(227,111,44,0.15)' : 'transparent',
                    border: 'none', cursor: 'pointer', font: 'inherit'
                  }}
                >
                  Other Systems <span style={{ fontSize: 10, marginLeft: 2 }}>▾</span>
                </button>
                {openGroup === 'Other Systems' && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, marginTop: 4,
                    background: '#fff', border: `1px solid ${colors.border}`, borderRadius: 6,
                    minWidth: 180, boxShadow: '0 8px 20px rgba(0,0,0,0.15)', zIndex: 30,
                    overflow: 'hidden'
                  }}>
                    {OTHER_SYSTEMS.map(({ label, url }) => (
                      <a key={url} href={url} target="_blank" rel="noopener noreferrer" onClick={() => setOpenGroup(null)} style={{
                        display: 'flex', justifyContent: 'space-between', gap: 12,
                        padding: '10px 14px', fontSize: 13, color: '#111827',
                        background: '#fff', fontWeight: 500, textDecoration: 'none',
                        borderBottom: `1px solid ${colors.border}`
                      }}>
                        <span>{label}</span>
                        <span style={{ color: colors.orange, fontSize: 12 }}>↗</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </nav>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, position: 'relative' }}>
          {!isMobile && <GlobalSearch />}
          <WhatsNew />
          <button onClick={() => setMenuOpen(o => !o)} style={{
            background: 'transparent', border: '1px solid #4B5563', color: '#fff',
            padding: isMobile ? '6px 10px' : '6px 14px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
            maxWidth: isMobile ? 110 : 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
          }}>
            {isMobile ? (
              <>{fmtRole(user.role)} ▾</>
            ) : (
              <>{user.full_name} <span style={{ color: colors.orange, fontWeight: 700 }}>· {fmtRole(user.role)}</span> ▾</>
            )}
          </button>
          {menuOpen && (
            <div style={{
              position: 'absolute', right: 0, top: '110%',
              background: '#fff', border: `1px solid ${colors.border}`, borderRadius: 6,
              minWidth: 250, boxShadow: '0 8px 20px rgba(0,0,0,0.15)', zIndex: 20
            }}>
              {/* The rep's own phone, under their own name — it is what the
                  customer reads on the Contact line of every quote they send,
                  and it was admin-only to set until now. Hidden while
                  previewing as someone else: that write is refused. */}
              {!user.is_preview && (
                <div style={{ padding: '10px 14px', borderBottom: `1px solid ${colors.border}` }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF',
                    letterSpacing: 1, textTransform: 'uppercase', marginBottom: 5 }}>
                    📞 My phone
                  </div>
                  <MyPhoneEditor width={132} />
                  <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 5, lineHeight: 1.4 }}>
                    Printed on your quotes as the customer's point of contact.
                  </div>
                </div>
              )}
              <button onClick={() => { setMenuOpen(false); setPwOpen(true); }} style={menuItem}>Change Password</button>
              {canImpersonate && !user.is_preview && (
                <button onClick={() => { setMenuOpen(false); setViewAsOpen(true); }} style={{ ...menuItem, borderTop: `1px solid ${colors.border}` }}>
                  👀 View as Account Manager
                </button>
              )}
              {user.is_preview && (
                <button onClick={() => { setMenuOpen(false); onExitPreview(); }} style={{ ...menuItem, borderTop: `1px solid ${colors.border}`, color: '#92400E' }}>
                  Exit Preview
                </button>
              )}
              <button onClick={() => { setMenuOpen(false); onLogout(); }} style={{ ...menuItem, borderTop: `1px solid ${colors.border}`, color: colors.red }}>Sign out</button>
            </div>
          )}
        </div>
        {isMobile && drawerOpen && (
          <div style={mobile.drawer}>
            {visibleNav.map(n => {
              if (n.kind === 'link') {
                return (
                  <NavLink
                    key={n.to} to={n.to}
                    onClick={() => setDrawerOpen(false)}
                    style={({ isActive }) => isActive
                      ? { ...mobile.drawerLink, ...mobile.drawerLinkActive }
                      : mobile.drawerLink}
                  >
                    {n.label}
                  </NavLink>
                );
              }
              // Group: render a small uppercase section header + the
              // children as flat links beneath. Keeps mobile a single
              // tap-list (no nested menus).
              return (
                <div key={n.label}>
                  <div style={{
                    padding: '12px 16px 4px', fontSize: 10, fontWeight: 700,
                    color: '#9CA3AF', letterSpacing: 1.5, textTransform: 'uppercase'
                  }}>
                    {n.label}
                  </div>
                  {n.children.map(c => (
                    <NavLink
                      key={c.to} to={c.to}
                      onClick={() => setDrawerOpen(false)}
                      style={({ isActive }) => isActive
                        ? { ...mobile.drawerLink, ...mobile.drawerLinkActive, paddingLeft: 28 }
                        : { ...mobile.drawerLink, paddingLeft: 28 }}
                    >
                      {c.label}
                    </NavLink>
                  ))}
                </div>
              );
            })}
            {/* Other Systems at the bottom of the drawer, same header style as
                the Sales group above. New-tab anchors, never NavLink. */}
            <div>
              <div style={{
                padding: '12px 16px 4px', fontSize: 10, fontWeight: 700,
                color: '#9CA3AF', letterSpacing: 1.5, textTransform: 'uppercase'
              }}>
                Other Systems
              </div>
              {OTHER_SYSTEMS.map(({ label, url }) => (
                <a key={url} href={url} target="_blank" rel="noopener noreferrer" onClick={() => setDrawerOpen(false)} style={{ ...mobile.drawerLink, paddingLeft: 28 }}>
                  {label} <span style={{ color: colors.orange, fontSize: 12 }}>↗</span>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
      {user.is_preview && (
        <div style={{
          background: '#FEF3C7', color: '#92400E', padding: '8px 16px',
          fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: '1px solid #F59E0B'
        }}>
          <span>
            👀 <strong>Previewing as {user.full_name}</strong>
            <span style={{ marginLeft: 8, color: '#92400E' }}>
              ({user.role}) — you're seeing exactly what they see
            </span>
          </span>
          <button onClick={onExitPreview} style={{
            background: '#92400E', color: '#fff', border: '1px solid #78350F', padding: '4px 12px',
            borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer', boxShadow: '0 1px 2px rgba(17, 24, 39, 0.12), 0 2px 4px rgba(17, 24, 39, 0.06)'
          }}>Exit Preview</button>
        </div>
      )}
      <div style={
        isMobile ? { ...styles.container, ...mobile.container }
          // Estimating's dense financial tables (labor ~18 cols) overflow the
          // centered 1400px cap and force horizontal scrolling — let this page
          // use the full screen width (George 2026-06-29). Other pages unchanged.
          : location.pathname.startsWith('/estimating') || location.pathname.startsWith('/price-book')
            ? { ...styles.container, maxWidth: 'none' }
          : styles.container
      }>
        {/* key={location.pathname} resets the boundary when route changes,
            so an error on one page doesn't stick when navigating away. */}
        <ErrorBoundary key={location.pathname}>
          <Outlet />
        </ErrorBoundary>
      </div>
      {pwOpen && <ChangePasswordModal onClose={() => setPwOpen(false)} />}
      {viewAsOpen && <ViewAsModal onClose={() => setViewAsOpen(false)} />}
    </>
  );
}

const menuItem = {
  display: 'block', width: '100%', textAlign: 'left',
  background: '#fff', border: 'none', padding: '10px 14px',
  cursor: 'pointer', fontSize: 13, color: '#111827'
};
